import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);

export default function LatePunch({ projectId, year, month, projects = [] }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    if (!projectId) return;
    try {
      const r = await api.get("/late/history", {
        params: { project_id: projectId, year, month },
      });
      setData(r.data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [projectId, year, month]);

  const dates = useMemo(() => {
    const n = data?.days_in_month || 0;
    return Array.from({ length: n }, (_, i) => `${year}-${pad(month)}-${pad(i + 1)}`);
  }, [data, year, month]);

  const lateColor = (mins) => {
    if (mins == null) return "";
    if (mins <= 15) return "bg-yellow-200 text-yellow-900";
    if (mins <= 30) return "bg-amber-300 text-amber-900";
    if (mins <= 60) return "bg-orange-400 text-white";
    return "bg-red-500 text-white";
  };

  const project = projects.find((p) => p.id === projectId);
  const lateEnabled = project?.late_tracking_enabled;

  return (
    <div className="space-y-5" data-testid="late-punch-page">
      {!lateEnabled && project && (
        <Card className="p-4 bg-amber-50 border-amber-200 text-sm text-amber-900">
          Late tracking is OFF for <strong>{project.name}</strong>. Enable it in
          Projects → Edit to start collecting late-punch records.
        </Card>
      )}

      <Card className="p-3 flex items-center gap-3 text-xs text-[#475569] flex-wrap">
        <span className="font-semibold">Legend:</span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-4 h-4 rounded bg-yellow-200 border" /> ≤ 15 min
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-4 h-4 rounded bg-amber-300 border" /> ≤ 30 min
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-4 h-4 rounded bg-orange-400 border" /> ≤ 60 min
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-4 h-4 rounded bg-red-500 border" /> &gt; 60 min
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-4 h-4 rounded bg-white border" /> on time / no record
        </span>
      </Card>

      {!data || data.workers.length === 0 ? (
        <Card className="p-10 text-center text-[#475569]">
          No employees on this project.
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="px-3 pt-3 pb-2 flex items-center justify-between gap-3 bg-slate-50/40 border-b">
            <Input
              data-testid="late-search"
              placeholder="Search employees…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="max-w-xs"
            />
            <div className="text-xs text-[#475569]">
              {(() => {
                const ql = q.trim().toLowerCase();
                const c = ql
                  ? data.workers.filter((w) =>
                      `${w.name} ${w.worker_id}`.toLowerCase().includes(ql)
                    ).length
                  : data.workers.length;
                return `${c} / ${data.workers.length}`;
              })()}
            </div>
          </div>
          <table className="w-full border-collapse table-fixed">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-semibold text-[#475569] sticky left-0 bg-slate-50 z-10 w-[160px]">
                  Employee
                </th>
                {dates.map((d) => (
                  <th
                    key={d}
                    className="px-0.5 py-2 text-[10px] font-semibold text-[#475569]"
                  >
                    {parseInt(d.split("-")[2])}
                  </th>
                ))}
                <th className="px-2 py-2 text-[11px] font-bold text-red-700 bg-red-50 sticky right-0 z-10 w-[64px]">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const ql = q.trim().toLowerCase();
                const filtered = ql
                  ? data.workers.filter((w) =>
                      `${w.name} ${w.worker_id}`.toLowerCase().includes(ql)
                    )
                  : data.workers;
                if (filtered.length === 0) {
                  return (
                    <tr>
                      <td colSpan={dates.length + 2} className="px-3 py-10 text-center text-[#475569]">
                        No employees match "{q}".
                      </td>
                    </tr>
                  );
                }
                return filtered.map((w) => (
                  <tr key={w.id} className="border-t" data-testid={`late-row-${w.worker_id}`}>
                    <td className="px-2 py-1 sticky left-0 bg-white z-10 border-r">
                      <div className="text-xs font-semibold text-[#1A3A5C] truncate max-w-[150px]">
                        {w.name}
                      </div>
                      <div className="text-[10px] font-mono text-[#475569]">
                        {w.worker_id}
                      </div>
                    </td>
                    {dates.map((d) => {
                      const g = data.grid[w.id]?.[d];
                      const mins = g?.late_minutes;
                      return (
                        <td
                          key={d}
                          className={`px-0.5 py-2 text-center text-[10px] font-bold border ${lateColor(mins)}`}
                          title={mins != null ? `${d}: ${mins} min late` : d}
                        >
                          {mins != null ? mins : ""}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1 text-center text-xs font-black text-red-700 bg-red-50 sticky right-0 z-10 border-l">
                      {data.totals_by_worker[w.id] || 0}
                    </td>
                  </tr>
                ));
              })()}
              {/* Totals row */}
              <tr className="border-t bg-amber-50/60 font-bold">
                <td className="px-2 py-2 sticky left-0 bg-amber-100 z-10 text-xs text-[#1A3A5C] border-r">
                  Total late / day
                </td>
                {dates.map((d) => (
                  <td key={d} className="px-0.5 py-1 text-center text-[10px] text-red-700">
                    {data.totals_by_day[d] || ""}
                  </td>
                ))}
                <td className="px-2 py-2 text-center text-xs text-red-700 bg-red-100 sticky right-0 z-10 border-l">
                  {Object.values(data.totals_by_worker).reduce((a, b) => a + b, 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
